import AirIcon from '@mui/icons-material/Air';
import LightModeIcon from '@mui/icons-material/LightMode';
import OpacityIcon from '@mui/icons-material/Opacity';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { Box, Stack } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import BayerRecommendation from '/@/components/crop-monitoring/bayer-reco';
import FieldCard from '/@/components/crop-monitoring/field-card-detail';
import CardGraphic from '/@/components/crop-monitoring/graphic/card-graphic';
import { BASE_STEPS, getActiveStepIndex } from '/@/components/crop-monitoring/progress-bar/assets/BASE_STEPS.js';
import colors from '/@/components/theme-provider/colors';
import useUnitContext from '/@/core/context/unit';
import { useCornCropMonitoringAlertConditions } from '/@/core/crop-monitoring';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { getCropProtectionProducts } from '/@/store/crop-monitoring/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { objectEntries } from '/@/utils';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import useIsMobile from '/@/utils/hooks/is-mobile';
import useWindowSize from '/@/utils/hooks/window-size';
import NewWeather from '../../weather';
import AlertsAndRisks from '../alerts-and-risks';
import FieldStatus from '../field-status';
import GDUCard from '../gdu-card';
import GraphicCards from '../graphic/graphic-cards';
import StageActionCard from '../stage-action-card';

/**
 *
 * @param {{
 * historicalWeather: import('hooks/weather').HistoricalWeatherQuery
 * }}
 */
const CropMonitoringCards = ({
    fieldId,
    cropMonitoring,
    technology,
    historicalWeather,
    setYieldEstimationDrawerIsOpen,
    setCMFormModalIsOpen,
}) => {
    const isMobile = useIsMobile();
    const windowWidth = useWindowSize().width;
    const isDesktopSm = windowWidth < 1200;
    const fieldQuery = useFieldById({ id: fieldId }, { enabled: !!fieldId });

    const country = useSelector(getSessionCountry);
    const [productsData, setProductsData] = useState([]);
    const [filteredProductsData, setFilteredProductsData] = useState([]);
    const [showCropProtection, setShowCropProtection] = useState(false);
    const cropProtectionProducts = useSelector(getCropProtectionProducts);
    const units = useUnitContext();
    const alertConditions = useCornCropMonitoringAlertConditions({ cropMonitoring });

    const weatherCardsConfiguration = useMemo(
        () =>
            ({
                temperature: {
                    title: _('Temperatura  ({{temperature}} / Días)', { temperature: units.temperature }),
                    quantity: 10,
                    icon: <LightModeIcon sx={{ fontSize: 24 }} />,
                    showYAxis: true,
                    domain: [units.toTemperature(-10, 0).value, units.toTemperature(50, 0).value],
                    graphicType: 'line',
                    max: units.toTemperature(35, 0).value,
                    min: units.toTemperature(3, 0).value,
                    dataTestId: 'weather-temperature-card',
                    references: [
                        { label: _('Riesgo de stress térmico'), color: 'red', dashed: true },
                        { label: _('Riesgo de helada'), color: 'cornflowerblue', dashed: true },
                        { label: _('Temperatura máxima'), color: 'red' },
                        { label: _('Temperatura mínima'), color: 'cornflowerblue' },
                    ],
                },
                rain: {
                    title: _('Precipitaciones  ({{rain}} / Días)', { rain: units.rain }),
                    dataTestId: 'weather-rain-card',
                    quantity: 10,
                    icon: <WaterDropIcon sx={{ fontSize: 24 }} />,
                    showYAxis: true,
                    domain: [0, units.toRain(100, 0).value],
                    references: [
                        { label: _('Precipitaciones acumuladas'), color: '#7AB3FF' },
                        { label: _('Precipitaciones diarias'), color: colors.themes.cultivio.primary.default },
                    ],
                },
                wind: {
                    title: _('Viento  ({{speed}} / Días)', { speed: units.speed }),
                    dataTestId: 'weather-wind-card',
                    quantity: 10,
                    icon: <AirIcon sx={{ fontSize: 24 }} />,
                    showYAxis: true,
                    domain: [0, units.toSpeed(60, 0).value],
                    max: units.toSpeed(39, 0)?.value,
                    graphicType: 'line',
                    references: [{ label: _('Vientos fuertes'), color: 'red' }],
                },
                humidity: {
                    title: _('Humedad  (% / Días)'),
                    dataTestId: 'weather-humidity-card',
                    quantity: 10,
                    icon: <OpacityIcon sx={{ fontSize: 24 }} />,
                    showYAxis: true,
                    domain: [0, 100],
                    graphicType: 'line',
                    max: null,
                    references: [{ label: _('Humedad'), color: colors.themes.cultivio.primary.default }],
                },
            }) as const,
        [units]
    );

    const crop = cropMonitoring?.crop;

    const cards = useMemo(() => {
        const cards = [
            ...(settings.app.theme !== 'expertobayer'
                ? [
                      {
                          id: 'field',
                          component: (
                              <FieldCard
                                  isMobile={isMobile}
                                  cropMonitoring={cropMonitoring}
                                  country={country}
                                  fields={[fieldQuery.data]}
                                  field={fieldQuery.data}
                              />
                          ),
                          enable: true,
                      },
                      {
                          id: 'bayer',
                          component: (
                              <BayerRecommendation
                                  productsData={filteredProductsData}
                                  isMobile={isMobile}
                                  cropMonitoring={cropMonitoring}
                              />
                          ),
                          enable: showCropProtection,
                      },
                  ]
                : [
                      {
                          id: 'field-status',
                          component: <FieldStatus field={fieldQuery.data} cropMonitoring={cropMonitoring} />,
                          enable: crop === 'corn' || !crop,
                      },
                      {
                          id: 'stage-action-card',
                          component: (
                              <StageActionCard
                                  cropMonitoring={cropMonitoring}
                                  field={fieldQuery.data}
                                  setYieldEstimationDrawerIsOpen={setYieldEstimationDrawerIsOpen}
                                  setCMFormModalIsOpen={setCMFormModalIsOpen}
                                  showHarvestRequest={alertConditions.showPrepareForYourHarvestAlert}
                              />
                          ),
                          enable: crop === 'corn' || !crop,
                      },
                  ]),
            {
                id: 'weather',
                component: (
                    <NewWeather
                        fieldId={fieldId}
                        sx={{
                            minHeight: 586,
                            ...(settings.app.theme !== 'expertobayer'
                                ? {
                                      boxShadow: { md: 'rgb(100 100 111 / 20%) 0px 7px 29px 0px' },
                                      borderRadius: 4,
                                  }
                                : {}),
                        }}
                    />
                ),
                enable: true,
            },
            ...(settings.app.theme === 'expertobayer'
                ? [
                      {
                          id: 'AlertsAndRisks',
                          component: (
                              <AlertsAndRisks
                                  field={fieldQuery.data || cropMonitoring?.field}
                                  crop={cropMonitoring?.crop}
                              />
                          ),
                          enable: true,
                      },
                  ]
                : []),
            ...(settings.app.theme === 'expertobayer'
                ? []
                : objectEntries(weatherCardsConfiguration).map(([type, config]) => ({
                      id: `graphic-${type}`,
                      component: <CardGraphic {...config} historicalWeather={historicalWeather} type={type} />,
                      enable: (historicalWeather?.results?.length || 0) !== 0,
                  }))),
        ];
        return cards.filter((c) => {
            return c.enable;
        });
    }, [
        alertConditions,
        country,
        cropMonitoring,
        fieldId,
        fieldQuery.data,
        filteredProductsData,
        historicalWeather,
        isMobile,
        setCMFormModalIsOpen,
        setYieldEstimationDrawerIsOpen,
        showCropProtection,
        weatherCardsConfiguration,
    ]);

    const getProductsData = () => {
        if (!cropMonitoring) return;
        const cropData = getCropMonitoringData(cropMonitoring);
        if (!cropData?.stages) {
            setProductsData([]);
            setFilteredProductsData([]);
            setShowCropProtection(false);
            return;
        }
        const lastStage = cropData?.stages[cropData?.stages?.length - 1];

        const active = () => {
            if (cropData?.stages.length > 0) {
                return getActiveStepIndex(lastStage);
            }
            return 0;
        };
        const filteredData = cropProtectionProducts.filter((d) => {
            return d.stage.name == BASE_STEPS[active()]?.state;
        });
        const filteredByTech = filteredData.filter((d) => {
            return d.technology.name == technology;
        });
        setFilteredProductsData(filteredByTech);
        setShowCropProtection(Boolean(filteredByTech.length));
        setProductsData(filteredData);
    };

    useEffect(() => {
        const filteredByTech = productsData.filter((d) => {
            return d.technology.name == technology;
        });
        setFilteredProductsData(filteredByTech);
        setShowCropProtection(Boolean(filteredByTech.length));
    }, [technology]);

    useEffect(() => {
        getProductsData();
    }, [cropProtectionProducts]);

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    gap: 4,
                    mb: 4,
                }}
            >
                {cards.map(({ component, id }) => {
                    return React.cloneElement(component, {
                        key: id,
                    });
                })}
            </Box>

            {settings.app.theme === 'expertobayer' && (historicalWeather?.results?.length || 0) !== 0 && (
                <Stack spacing={4}>
                    <GDUCard
                        historicalWeather={historicalWeather}
                        cropMonitoring={cropMonitoring}
                        isPdf={false}
                        isMobile={isMobile}
                        isDesktopSm={isDesktopSm}
                    />
                    <GraphicCards
                        weatherCardsConfiguration={weatherCardsConfiguration}
                        historicalWeather={historicalWeather}
                    />
                </Stack>
            )}
        </>
    );
};

export default CropMonitoringCards;
